import { createSelector } from "@reduxjs/toolkit";

import {
  DEFAULT_ORDER_FN,
  ORDER_BY_CREATED,
  ORDER_BY_CREATED_DATE_DESC,
  ORDER_BY_NAME,
  ORDER_BY_TITLE,
} from "mds/hooks/use-ordered";
import { RootState } from "store";
import {
  ModelType,
  PageScopedModelType,
  PrincipalOwnedModelType,
  SectionScopedModelType,
  UserScopedModelType,
} from "utils/type-utils";

export const createAppSelector = createSelector.withTypes<RootState>();

export const selectCurrentUserId = (state: RootState) => state.auth.user_id;
export const selectUserJWT = (state: RootState) => state.auth.collaboration_jwt;

// This needs to be in base.ts instead of view.ts to avoid circular dependencies
export const selectCurrentSectionId = (state: RootState) => state.view.currentSectionId;

// Unified set of selectors for all slices

type SliceSelector<T> = (state: RootState) => Record<string, T>;
type SelectorOptionsType<T> = {
  sortingFunction?: (a: T, b: T) => number;
};

export const createSelectEntities = <T>(getSlice: SliceSelector<T>) => getSlice;

const createSelectById =
  <T>(getSlice: SliceSelector<T>) =>
  (state: RootState, id: string | null | undefined): T | null => {
    const slice = getSlice(state);
    return id && id in slice ? slice[id] : null;
  };

const createSelectAll = <T>(getSlice: SliceSelector<T>, options?: SelectorOptionsType<T>) =>
  createAppSelector([createSelectEntities(getSlice)], (slice) => {
    const values = Object.values(slice);
    if (options?.sortingFunction) values.sort(options.sortingFunction);
    return values;
  });

const createFilterSelector = <T>(
  getSlice: SliceSelector<T>,
  attribute: keyof T,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [
      createSelectAll(getSlice, options),
      (_state: RootState, value: T[keyof T] | null | undefined) => value,
    ],
    (allObjects, value) => allObjects.filter((item) => item[attribute] === value),
  );

const createFilterForCurrentSectionSelector = <T extends { section_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [createSelectAll(getSlice, options), selectCurrentSectionId],
    (allObjects, currentSectionId) =>
      currentSectionId ? allObjects.filter((item) => item.section_id === currentSectionId) : [],
  );

const createFilterForCurrentUserSelector = <T extends { user_id: string }>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) =>
  createAppSelector(
    [createSelectAll(getSlice, options), selectCurrentUserId],
    (allObjects, currentUserId) => allObjects.filter((item) => item.user_id === currentUserId),
  );
// Base selectors for all slices/models
const createBaseSelectors = <T extends ModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    selectById: createSelectById(getSlice),
    selectAll: createSelectAll(getSlice, options),
    selectEntities: createSelectEntities(getSlice),
  };
};

// Selectors for slices/models that are scoped to a section
const createSectionScopedSelectors = <T extends SectionScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createBaseSelectors(getSlice, options),
    filterBySectionId: createFilterSelector<T>(getSlice, "section_id", options),
    filterForCurrentSection: createFilterForCurrentSectionSelector<T>(getSlice, options),
  };
};

// Selectors for slices/models that are scoped to a user
const createUserScopedSelectors = <T extends UserScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createBaseSelectors(getSlice, options),
    filterByUserId: createFilterSelector<T>(getSlice, "user_id", options),
    filterForCurrentUser: createFilterForCurrentUserSelector<T>(getSlice, options),
  };
};

// Selectors for slices/models that are scoped to a page
const createPageScopedSelectors = <T extends PageScopedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createSectionScopedSelectors(getSlice, options),
    filterByPageId: createFilterSelector<T>(getSlice, "page_id", options),
  };
};

// Selectors for principal-owned slices/models
const createPrincipalOwnedPageScopedSelectors = <T extends PrincipalOwnedModelType>(
  getSlice: SliceSelector<T>,
  options?: SelectorOptionsType<T>,
) => {
  return {
    ...createPageScopedSelectors(getSlice, options),
    filterBySectionUserId: createFilterSelector<T>(getSlice, "section_user_id", options),
    filterByPageGroupId: createFilterSelector<T>(getSlice, "page_group_id", options),
  };
};

export const activitySelectors = createSectionScopedSelectors((s) => s.activities);
export const assessmentSelectors = createSectionScopedSelectors((s) => s.assessments);
export const breakoutGroupSelectors = createSectionScopedSelectors((s) => s.breakout_groups);
export const chatMessageSelectors = createSectionScopedSelectors((s) => s.chat_messages, {
  sortingFunction: ORDER_BY_CREATED_DATE_DESC,
});
export const classSessionSelectors = createSectionScopedSelectors((s) => s.class_sessions);
export const classSessionSummarySelectors = createSectionScopedSelectors(
  (s) => s.class_session_summaries,
);
export const classSessionUserSummarySelectors = {
  ...createSectionScopedSelectors((s) => s.class_session_user_summaries),
  ...createUserScopedSelectors((s) => s.class_session_user_summaries),
};
export const classUserSelectors = createSectionScopedSelectors((s) => s.class_users);
export const courseGroupingSelectors = createBaseSelectors((s) => s.course_groupings);
export const courseSelectors = createBaseSelectors((s) => s.courses);
export const orgUserSelectors = createUserScopedSelectors((s) => s.org_users);
export const orgSelectors = createBaseSelectors((s) => s.orgs, {
  sortingFunction: ORDER_BY_NAME,
});
export const outcomeAssessmentSelectors = createBaseSelectors((s) => s.outcome_assessments);
export const outcomeGroupSelectors = createBaseSelectors((s) => s.outcome_groups, {
  sortingFunction: ORDER_BY_TITLE,
});
export const outcomeSubgroupSelectors = createBaseSelectors((s) => s.outcome_subgroups, {
  sortingFunction: ORDER_BY_TITLE,
});
export const outcomeSelectors = createBaseSelectors((s) => s.outcomes, {
  sortingFunction: ORDER_BY_TITLE,
});
export const pageAccessDataSelectors = createPrincipalOwnedPageScopedSelectors(
  (s) => s.page_access_data,
);
export const pageGroupUserSelectors = createSectionScopedSelectors((s) => s.page_group_users);
export const pageGroupSelectors = createPageScopedSelectors((s) => s.page_groups, {
  sortingFunction: ORDER_BY_CREATED,
});
export const pageOutcomeSelectors = createPageScopedSelectors((s) => s.page_outcomes);
export const pageSelectors = createSectionScopedSelectors((s) => s.pages, {
  sortingFunction: DEFAULT_ORDER_FN,
});
export const recordingSessionSelectors = createSectionScopedSelectors((s) => s.recording_sessions);
export const sectionOutcomeSelectors = createSectionScopedSelectors((s) => s.section_outcomes);
export const sectionSelectors = createBaseSelectors((s) => s.sections, {
  sortingFunction: ORDER_BY_TITLE,
});
export const sectionUserSelectors = {
  ...createSectionScopedSelectors((s) => s.section_users),
  ...createUserScopedSelectors((s) => s.section_users),
};
export const submissionSelectors = createPrincipalOwnedPageScopedSelectors((s) => s.submissions);
export const termSelectors = createBaseSelectors((s) => s.terms);
export const topicSelectors = createSectionScopedSelectors((s) => s.topics);
export const userSelectors = createBaseSelectors((s) => s.users);

export const selectHasCourses = createAppSelector(
  [courseSelectors.selectAll],
  (courses) => courses.length > 0,
);

export const selectBulkImportJobById = createSelectById((state) => state.bulk_import_jobs);
